import AppsIcon from "@mui/icons-material/Apps";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import { AllCategories, Category, MarketplaceCategories, PostCategories } from "config";
import React, { useCallback, useEffect, useRef, useState } from "react";

const CategoryDropdown = styled(Button)(({ theme }) => ({
  backgroundColor: "#E19941",
  borderBottomRightRadius: 0,
  borderTopRightRadius: 0,
  color: theme.palette.common.white,
  height: "56px",
  fontSize: "0.9em",
  minWidth: "12rem",
  "&:hover": {
    backgroundColor: "#E19941",
  },
}));
const CategoryDropdownSmall = styled(Button)(({ theme }) => ({
  backgroundColor: "#E19941",
  color: theme.palette.common.white,
  height: "56px",
  marginRight: "1rem",
  "&:hover": {
    backgroundColor: "#E19941",
  },
  padding: 0,
}));
const SearchBox = styled(TextField)(({ theme }) => ({
  boxSizing: "border-box",
  width: "100%",
  "& label.Mui-focused": {
    color: theme.palette.common.black,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#E19941",
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
    "&:hover fieldset": {
      borderColor: "#E19941",
      borderWidth: "1px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E19941",
      borderWidth: "1px",
      boxShadow: "inset -1px 0px 0px 1px #E19941",
      outline: 0,
    },
  },
}));
const CategoryMenu = styled(Menu)(() => ({
  zIndex: 10,
}));

type SearchBarProps = {
  embedded?: boolean;
  searchParams?: URLSearchParams;
  searchTerm: string;
  onCategoryChanged: (category: Category) => void;
  onSearch: (searchTerm: string) => void;
  onSearchCleared: (searchTerm: string) => void;
};

export default function SearchBar(props: Readonly<SearchBarProps>) {
  const { embedded, searchParams, onCategoryChanged, onSearch, onSearchCleared } = props;

  const anchorRef = useRef<HTMLButtonElement>(null);
  const categories = AllCategories.concat(MarketplaceCategories, PostCategories);

  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [categoryMenuAnchorEl, setCategoryMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>(() => props.searchTerm);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const initFromQueryString = useCallback(() => {
    if (!searchParams) return;

    if (searchParams.has("query")) {
      setSearchTerm(searchParams.get("query") ?? "");
    }

    const selectedCategory = categories.find((c) => c.id === searchParams.get("category"));

    if (!selectedCategory) return;

    setSelectedCategory(selectedCategory);
    onCategoryChanged(selectedCategory);
  }, [categories, onCategoryChanged, searchParams]);

  useEffect(() => {
    setTimeout(() => {
      initFromQueryString();
    }, 0);
  }, [initFromQueryString]);

  function handleMenuClose(event: React.MouseEvent) {
    if (categoryMenuAnchorEl?.contains(event.target as Node)) return;
    setCategoryMenuOpen(false);
    setCategoryMenuAnchorEl(null);
  }

  function handleCategorySelected(event: React.MouseEvent, id: string) {
    handleMenuClose(event);
    const selectedCategory = categories.find((category) => category.id === id);
    if (!selectedCategory) return;
    setSelectedCategory(selectedCategory);
    setSearchTerm("");
    onCategoryChanged(selectedCategory);
  }

  return (
    <Grid container direction="row" justifyContent="space-between" alignItems="center">
      <Grid>
        {!embedded && (
          <>
            {!isMediumDisplaySize && (
              <CategoryDropdown
                ref={anchorRef}
                disableElevation
                variant="contained"
                endIcon={<ArrowDropDownIcon />}
                onClick={(event) => {
                  setCategoryMenuOpen(true);
                  setCategoryMenuAnchorEl(event.currentTarget);
                }}
              >
                {selectedCategory ? selectedCategory.name : "All Categories"}
              </CategoryDropdown>
            )}
            {isMediumDisplaySize && (
              <CategoryDropdownSmall
                ref={anchorRef}
                disableElevation
                variant="contained"
                onClick={(event) => {
                  setCategoryMenuOpen(true);
                  setCategoryMenuAnchorEl(event.currentTarget);
                }}
              >
                <AppsIcon />
              </CategoryDropdownSmall>
            )}
          </>
        )}
      </Grid>
      <Grid size="grow">
        <SearchBox
          variant="outlined"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === KeyboardKey.Enter) onSearch(searchTerm);
            if (event.key === KeyboardKey.Escape) setSearchTerm("");
          }}
          slotProps={{
            input: {
              placeholder: selectedCategory ? selectedCategory.description : "Search Entire Marketplace",
              sx: {
                backgroundColor: "#fff",
                "& :placeholder-shown": {
                  textOverflow: "ellipsis",
                },
              },
              startAdornment: (
                <IconButton onClick={() => onSearch(searchTerm)} disabled={searchTerm === ""} size="large">
                  <SearchIcon />
                </IconButton>
              ),
              endAdornment: (
                <IconButton
                  style={{ display: searchTerm === "" ? "none" : "flex" }}
                  onClick={() => {
                    setSearchTerm("");
                    onSearchCleared("");
                  }}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              ),
            },
          }}
        />
        <CategoryMenu
          keepMounted
          open={categoryMenuOpen}
          onClose={handleMenuClose}
          anchorEl={categoryMenuAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              style: {
                maxHeight: 48 * 10,
              },
            },
          }}
        >
          {categories.map((category) => (
            <MenuItem key={category.id} onClick={(event) => handleCategorySelected(event, category.id)}>
              {category.name}
            </MenuItem>
          ))}
        </CategoryMenu>
      </Grid>
    </Grid>
  );
}
